import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { ApplicationModel } from '../types';

/**
 * Scrolls the viewport to the first validation error when the user attempts to submit
 * with invalid data. Uses submitCount (not isSubmitting) because Formik never calls
 * onSubmit when validation fails, so isSubmitting stays false.
 */
export const ErrorScrollWrapper: React.FunctionComponent<{}> = props => {
  const { children } = props;
  const context = useFormikContext<ApplicationModel>();
  const lastScrolledErrorsRef = useRef<string>('');

  useEffect(() => {
    const hasErrors = Object.keys(context.errors).length > 0;
    const userAttemptedSubmit = context.submitCount > 0;
    const validationComplete = !context.isValidating;

    if (hasErrors && userAttemptedSubmit && validationComplete) {
      // Avoid re-scrolling for the same error set (e.g. rapid re-renders)
      const errorKeys = Object.keys(context.errors).sort().join(',');
      if (errorKeys === lastScrolledErrorsRef.current) {
        return;
      }
      lastScrolledErrorsRef.current = errorKeys;

      // Prefer invalid form field (shows the input), fallback to error message text
      const errorElement =
        (document.querySelector('.flabel.invalid, .invalid') as HTMLElement) ||
        (document.querySelector('.error') as HTMLElement);

      if (errorElement) {
        setTimeout(() => {
          try {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } catch (err) {
            console.warn('[ErrorScrollWrapper] Scroll to error failed:', err);
          }
        }, 100);
      }
    } else if (!hasErrors) {
      lastScrolledErrorsRef.current = '';
    }
  }, [context.errors, context.submitCount, context.isValidating]);

  return (
    <>
      <div>{children}</div>
    </>
  );
};
